import React, { useState } from 'react'
import RepairKitRequestForm from './repair-kit-request-form'
import {
  RepairKitRequestFormValues,
  buildKitRequestApiPayload,
} from './repair-kit-form-shared'
import { useUpsertSd500KitRequestDraft } from '../../api/sd500-kit-request'

interface Sd500RepairKitRequestStepProps {
  initialEmail?: string
  initialCountry?: string
  language: string
  submissionId?: string
  quantity?: Record<string, number>
  savedValues?: Partial<RepairKitRequestFormValues>
  onSaveValues?: (values: RepairKitRequestFormValues) => void
  onDraftSaved: (kitRequestId: string, values: RepairKitRequestFormValues) => void
}

/**
 * SD500 / EMD500E wrapper: persists a kit-request draft before moving to confirmation.
 */
const Sd500RepairKitRequestStep: React.FC<Sd500RepairKitRequestStepProps> = ({
  initialEmail,
  initialCountry,
  language,
  submissionId,
  quantity = {},
  savedValues,
  onSaveValues,
  onDraftSaved,
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null)

  const draftMutation = useUpsertSd500KitRequestDraft()

  const handleSuccess = async (values: RepairKitRequestFormValues) => {
    if (!submissionId || draftMutation.isPending) {
      return
    }

    setSubmitError(null)

    try {
      const saved = await draftMutation.mutateAsync({
        data: buildKitRequestApiPayload(values, quantity, language, submissionId),
      })
      const kitRequestId = String(saved._id ?? (saved as { id?: string }).id ?? '')
      if (!kitRequestId) {
        throw new Error('Missing kit request id in draft response')
      }
      onDraftSaved(kitRequestId, values)
    } catch {
      setSubmitError('submit_failed')
    }
  }

  return (
    <>
      {submitError && (
        <p role="alert" style={{ color: '#cc0f35', marginBottom: '1rem' }}>
          {submitError}
        </p>
      )}
      <RepairKitRequestForm
        initialEmail={initialEmail}
        initialCountry={initialCountry}
        language={language}
        savedValues={savedValues}
        onSaveValues={onSaveValues}
        onSuccess={handleSuccess}
        isSubmitting={draftMutation.isPending}
      />
    </>
  )
}

export default Sd500RepairKitRequestStep
